import React from "react";
import ReactDiffViewer, { DiffMethod, ReactDiffViewerStylesOverride, ReactDiffViewerProps } from 'react-diff-viewer-continued';

interface DiffViewerProps extends Omit<ReactDiffViewerProps, 'compareMethod'> {
    /**
     * Comparison method: 'lines', 'words', 'chars' (default: 'lines')
     * Simplified version of ReactDiffViewer's compareMethod prop
     */
    compareMethod?: 'lines' | 'words' | 'chars';
}

/**
 * A clean, reusable diff viewer component with syntax highlighting for comparing two text values.
 *
 * Features:
 * - Side-by-side or unified comparison view
 * - Line, word, or character-level diffing
 * - Responsive design (automatically switches to unified view on mobile)
 * - Customizable titles and appearance
 * - Mobile-friendly with text wrapping
 * - No horizontal scroll
 *
 * @example
 * ```tsx
 * // Simple usage
 * <DiffViewer
 *   oldValue={JSON.stringify(oldData, null, 2)}
 *   newValue={JSON.stringify(newData, null, 2)}
 * />
 *
 * // With custom titles
 * <DiffViewer
 *   oldValue={oldCode}
 *   newValue={newCode}
 *   leftTitle="Version 1.0"
 *   rightTitle="Version 2.0"
 * />
 *
 * // Unified view with word diff
 * <DiffViewer
 *   oldValue={text1}
 *   newValue={text2}
 *   splitView={false}
 *   compareMethod="words"
 * />
 * ```
 */
const DiffViewer = React.memo(
    function DiffViewer({
                            compareMethod = 'lines',
                            splitView,
                            styles,
                            leftTitle = 'Original',
                            rightTitle = 'Updated',
                            ...otherProps
                        }: DiffViewerProps) {

        // Auto-detect mobile and use unified view
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        const shouldSplitView = splitView !== undefined ? splitView : !isMobile;

        // Map compare method to DiffMethod enum
        const diffMethod = compareMethod === 'words'
            ? DiffMethod.WORDS
            : compareMethod === 'chars'
                ? DiffMethod.CHARS
                : DiffMethod.LINES;

        // Default styles for mobile-friendly display
        const defaultStyles: ReactDiffViewerStylesOverride = {
            contentText: {
                wordBreak: 'break-word',
                wordWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
            },
            line: {
                wordBreak: 'break-word',
                wordWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
            }
        };

        // Merge default styles with custom styles
        const mergedStyles: ReactDiffViewerStylesOverride = styles
            ? {
                ...defaultStyles,
                ...styles,
                contentText: { ...(defaultStyles.contentText as any), ...(styles.contentText as any) },
                line: { ...(defaultStyles.line as any), ...(styles.line as any) }
            }
            : defaultStyles;

        return (
            <div style={{
                width: '100%',
                overflow: 'hidden'
            }}>
                <ReactDiffViewer
                    {...otherProps}
                    splitView={shouldSplitView}
                    leftTitle={leftTitle}
                    rightTitle={rightTitle}
                    compareMethod={diffMethod}
                    styles={mergedStyles}
                />
            </div>
        );
    },
    (prevProps, nextProps) => {
        // Deep comparison of all props
        return JSON.stringify(prevProps) === JSON.stringify(nextProps);
    }
);

export default DiffViewer;